import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { acct, SUBTYPES, SUBTYPE_LABELS, TYPE_LABELS } from "@/lib/money";
import { Page, Btn, Table, Tr, Td, Field, Loading } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const input = "min-h-[40px] rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-800";

export default function Accounts() {
  const { company } = useCompany();
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    if (company) setRows((await api.get(`/companies/${company.id}/accounts`)).data);
  }, [company]);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Page title="Chart of accounts"><Loading /></Page>;
  const save = async (form) => {
    try {
      if (form.id) await api.put(`/companies/${company.id}/accounts/${form.id}`, { code: form.code, name: form.name, active: form.active });
      else await api.post(`/companies/${company.id}/accounts`, form);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <Page title="Chart of accounts" actions={<Btn kind="primary" onClick={() => setEditing({ code: "", name: "", type: "expense", subtype: "operating" })}>New account</Btn>}>
      <Table minWidth={720} cols={[{ label: "Code" }, { label: "Account" }, { label: "Type" }, { label: "Group" }, { label: "Balance", align: "right" }, { label: "" }]}>
        {rows.map((a) => (
          <Tr key={a.id} className={a.active === false ? "opacity-50" : ""}>
            <Td className="font-mono text-slate-500">{a.code}</Td>
            <Td><Link to={`/ledger/${a.id}`} className="font-medium text-[#1C1B1A] hover:text-brand hover:underline">{a.name}</Link>{a.active === false && <span className="ml-2 text-xs text-slate-500">closed</span>}</Td>
            <Td className="text-slate-500">{TYPE_LABELS[a.type]}</Td>
            <Td className="text-slate-500">{SUBTYPE_LABELS[a.subtype] || "—"}</Td>
            <Td right>{acct(a.balance)}</Td>
            <Td><button type="button" className="min-h-[34px] px-2 text-sm text-brand hover:underline" onClick={() => setEditing({ ...a })}>Edit</button></Td>
          </Tr>
        ))}
      </Table>
      {editing && <AccountDialog form={editing} onClose={() => setEditing(null)} onSave={save} />}
    </Page>
  );
}

function AccountDialog({ form: start, onClose, onSave }) {
  const [form, setForm] = useState(start);
  const isNew = !form.id;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{isNew ? "New account" : `${form.code} ${form.name}`}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-[110px_1fr] gap-3">
            <Field label="Code"><input className={input} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
            <Field label="Name"><input className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          </div>
          {isNew && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <select className={input} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, subtype: SUBTYPES[e.target.value][0] })}>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Group">
                <select className={input} value={form.subtype} onChange={(e) => setForm({ ...form, subtype: e.target.value })}>
                  {SUBTYPES[form.type].map((k) => <option key={k} value={k}>{SUBTYPE_LABELS[k]}</option>)}
                </select>
              </Field>
            </div>
          )}
          {!isNew && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active === false} onChange={(e) => setForm({ ...form, active: !e.target.checked })} /> Closed (no new entries)</label>
          )}
        </div>
        <DialogFooter><Btn onClick={onClose}>Cancel</Btn><Btn kind="primary" onClick={() => onSave(form)}>Save</Btn></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
